import { useRouter } from 'next/router';
import { useApiErrorMessageToast } from '~/pages/_shared';
import { ErrorMessages } from './useApiErrorMessageToast';
import { useToast } from '@chakra-ui/react';
import { useLocalization } from '~/framework';

type ServiceUnavailableErrorProps<ErrorCode extends string> = {
   errorMessages: ErrorMessages<ErrorCode>;
   isClosable?: boolean;
   redirect?: string;
   serviceName?: string;
};

export function useServiceUnavailableError<ErrorCode extends string>({
   errorMessages,
   isClosable,
   redirect,
   serviceName,
}: ServiceUnavailableErrorProps<ErrorCode>) {
   const router = useRouter();
   const toast = useToast();
   const { _js } = useLocalization();

   const { handleError: defaultToastHandler } = useApiErrorMessageToast(errorMessages, isClosable);

   function handleServiceUnavailableWithToast(error: any): void {
      if (error?.response?.status !== 503) {
         return defaultToastHandler(error);
      }

      const title = serviceName
         ? _js('%1 service is temporarily unavailable.', serviceName)
         : _js('Service is temporarily unavailable.');

      toast({
         title: title,
         status: 'error',
         position: 'top',
         isClosable: isClosable,
      });
   }

   function handleServiceUnavailableWithRedirect(error: any): void {
      if (error?.response?.status === 503) {
         if (redirect) {
            router.push(redirect);
            return;
         }

         router.push(`/errors/service-unavailable?name=${serviceName}`);
         return;
      }

      handleServiceUnavailableWithToast(error);
   }

   return {
      handleServiceUnavailableWithToast,
      handleServiceUnavailableWithRedirect,
   };
}
